import 'constants/constants.json';

export const ApiService = angular
    .module('checkout.apiService', [
        'app.constants'
    ]).service('apiService', (BASE_API_URL, $http) => {
        let tenantId;
        let shopperId;

        const apiUrls = {
            addressAutocomplete: getApiUrl('/addresses/autocomplete'),
            addressAutocompleteDetails: getApiUrl('/addresses/autocomplete/details'),
            addressVerification: getApiUrl('/addresses/addressverification'),
            legacyCart: getApiUrl('/carts'),
            cart: getApiUrl('/public/rest/carts'),
            discount: getApiUrl('/discounts'),
            bag: getApiUrl('/store/bag'),
            order: getApiUrl('/store/orders'),
            paypal: getApiUrl('/public/rest/paypal/orders'),
            paypalClientToken: getApiUrl('/paypal/getToken'),
            legacyPaypal: getApiUrl('/paypal'),
            storeInfo: getApiUrl('/storeinformation'),
            paymentKeys: getApiUrl('/store/paymentkeys'),
            shopperAuth: getApiUrl('/shopper/shoppers'),
            shopper: getApiUrl('/shoppers'),
            client: getApiUrl('/shoppers/client'),
            shipToLocations: getApiUrl('/store/shiptolocations'),
            shippingMethods: getApiUrl('/store/shippingmethods'),
            storeCredit: getApiUrl('/store/storeCredit'),
            variant: getApiUrl('/public/rest/variants')
        };

        function getApiUrl(urlEnding) {
            return `${BASE_API_URL}${urlEnding}`;
        }

        function getHttpConfig(skipAuth) {
            const config = {
                headers: {
                    'x-mat-tenant': tenantId
                },
                skipAuthorization: skipAuth
            };

            if (shopperId && !skipAuth) {
                config.headers['x-vol-shopper'] = shopperId;
            }

            return config;
        }

        function setTenantId(id) {
            tenantId = id;
        }

        function getTenantId() {
            return tenantId;
        }

        function setShopperId(id) {
            shopperId = id;
        }

        function getShopperId() {
            return shopperId;
        }

        function getLegacyCart(cartId) {
            return $http.get(`${apiUrls.legacyCart}/${cartId}`, getHttpConfig(true)).then(response => response.data);
        }

        function getCart(cartId) {
            const cartPath = `${apiUrls.cart}/${cartId}`;
            return $http.get(cartPath, getHttpConfig(true)).then((response) => {
                let cart = response.data;
                if (
                    typeof cart === 'object' &&
                    cart !== null &&
                    Array.isArray(cart.items)
                ) {
                    return Promise.all(
                        cart.items.map(item => getVariant(item))
                    ).then((itemResponses) => {
                        itemResponses.forEach((itemResponse, index) => {
                            cart.items[index].variant = itemResponse;
                        });
                        return cart;
                    });
                }
                return cart;
            });
        }

        function fetchShippingMethods({ cartId, country, state }) {
            const cartPath = `${apiUrls.cart}/${cartId}`;
            return $http.get(`${cartPath}/shippingmethods`, {
                ...getHttpConfig(true),
                params: { country, state }
            }).then(res => res.data.availableShippingMethods);
        }


        function getVariant(cartItem) {
            if (!cartItem.variantId) {
                return undefined;
            }
            return $http.get(`${apiUrls.variant}/${cartItem.variantId}`, getHttpConfig(true))
                .then(itemResponse => itemResponse.data);
        }

        function updateLegacyCart(cartId, data) {
            return $http.put(`${apiUrls.legacyCart}/${cartId}`, data, getHttpConfig(true))
                .then(response => response.data);
        }

        function updateCart(cartId, data) {
            return $http.patch(`${apiUrls.cart}/${cartId}`, data, getHttpConfig(true)).then(response => response.data);
        }

        function getClientId() {
            return $http.get(apiUrls.client, {
                ...getHttpConfig(true),
                transformResponse: angular.identity
            }).then((response) => {
                return response.data;
            });
        }

        function getBag(bagId) {
            return $http.get(`${apiUrls.bag}/${bagId}`, getHttpConfig(true)).then(response => response.data);
        }

        function createOrder(data) {
            return $http.post(apiUrls.order, data, getHttpConfig(true)).then(response => response.data);
        }

        function getOrder(orderNumber, orderEmail) {
            const query = `${encodeURIComponent(orderNumber)}?email=${encodeURIComponent(orderEmail)}`;
            return $http.get(`${apiUrls.order}/${query}`, getHttpConfig(true)).then(response => response.data);
        }

        function createPaypalExpressCheckout(data) {
            return $http.post(`${apiUrls.paypal}`, data, getHttpConfig(true))
                .then(response => response.data);
        }

        function legacyCreatePaypalExpressCheckout(data) {
            return $http.post(`${apiUrls.legacyPaypal}/setExpressCheckout`, data, getHttpConfig(true))
                .then(response => response.data);
        }

        function getPaypalExpressCheckout(data) {
            return $http.post(`${apiUrls.legacyPaypal}/getExpressCheckoutDetails`, data, getHttpConfig(true))
                .then(response => response.data);
        }

        function getPayPalClientToken() {
            return $http.post(`${apiUrls.paypalClientToken}`).then(
                response => response.data,
                response => new Error(`getPayPalClientToken: error: ${response}`)
            );
        }

        function getStoreInformation() {
            return $http.get(apiUrls.storeInfo, getHttpConfig(true)).then(response => response.data);
        }

        function getStripePublishableKey() {
            return $http.get(apiUrls.paymentKeys, getHttpConfig(true)).then(response => response.data);
        }

        function getDiscountValidity(shopperEmail, discountId) {
            const query = `email/${encodeURIComponent(shopperEmail)}/discount/${encodeURIComponent(discountId)}`;
            return $http.get(`${apiUrls.discount}/${query}`, getHttpConfig(true)).then(response => response.data);
        }

        function addDiscountCodeToCart(code, cartId) {
            return $http.post(`${apiUrls.cart}/${cartId}/discounts`, {
                discountCode: code
            }, getHttpConfig());
        }

        function removeDiscountCodeFromCart(code, cartId) {
            return $http.delete(`${apiUrls.cart}/${cartId}/discounts/${code}`, getHttpConfig());
        }

        function getStoreCreditByCode(code) {
            return $http.get(`${apiUrls.storeCredit}/${code}`, getHttpConfig(true)).then(response => response.data);
        }

        function getShipToLocations() {
            return $http.get(apiUrls.shipToLocations, getHttpConfig(true)).then(response => response.data);
        }

        // Shopper account functions

        function login(email, password) {
            return $http.post(`${apiUrls.shopper}/login`, { email, password }, getHttpConfig())
                .then(response => response.data);
        }

        function isEmailRegistered(email) {
            return $http.get(`${apiUrls.shopper}/registered/${email}`, getHttpConfig())
                .then(response => response.data);
        }

        function sendPasswordResetEmail(email) {
            return $http.post(`${apiUrls.shopper}/sendResetPasswordEmail/${email}`, {}, getHttpConfig())
                .then(response => response.data);
        }

        function verifyResetPasswordCode(email, code) {
            return $http.get(
                `${apiUrls.shopper}/validateResetPasswordToken/${email}/${code}`,
                getHttpConfig()
            ).then(response => response.data);
        }

        function updatePassword(email, password, code) {
            return $http.post(
                `${apiUrls.shopper}/resetShopperPassword/${email}/${code}`,
                { password },
                getHttpConfig()
            ).then(response => response.data);
        }

        function createShopper(emailAddress, password) {
            return $http.post(apiUrls.shopper, { emailAddress, password }, getHttpConfig())
                .then(response => response.data);
        }

        function getShopper() {
            return $http.get(`${apiUrls.shopperAuth}`, getHttpConfig()).then(response => response.data);
        }

        function updateShopper(shopper) {
            return $http.put(`${apiUrls.shopperAuth}`, shopper, getHttpConfig())
                .then(response => response.data);
        }

        function addPaymentMethod(creditCardToken, isDefault) {
            return $http.post(
                `${apiUrls.shopperAuth}/card`,
                { creditCardToken, isDefault },
                getHttpConfig()
            ).then(response => response.data);
        }

        function getPaymentMethodById(cardId) {
            return $http.get(`${apiUrls.shopperAuth}/card/${cardId}`, getHttpConfig())
                .then(response => response.data);
        }

        function getPaymentMethods() {
            return $http.get(`${apiUrls.shopperAuth}/card`, getHttpConfig())
                .then(response => response.data);
        }

        function updatePaymentMethod(cardId, paymentMethod) {
            return $http.put(`${apiUrls.shopperAuth}/card/${cardId}`, paymentMethod, getHttpConfig())
                .then(response => response.data);
        }

        function deletePaymentMethod(cardId) {
            return $http.delete(`${apiUrls.shopperAuth}/card/${cardId}`, getHttpConfig());
        }

        function addShippingAddress(shippingAddress) {
            return $http.post(`${apiUrls.shopperAuth}/shippingaddress`, shippingAddress, getHttpConfig())
                .then(response => response.data);
        }

        function getShippingAddressById(shippingAddressId) {
            return $http.get(
                `${apiUrls.shopperAuth}/shippingaddress/${shippingAddressId}`,
                getHttpConfig()
            ).then(response => response.data);
        }

        function getShippingAddresses() {
            return $http.get(`${apiUrls.shopperAuth}/shippingaddress`, getHttpConfig())
                .then(response => response.data);
        }

        function updateShippingAddress(shippingAddressId, shippingAddress) {
            return $http.put(
                `${apiUrls.shopperAuth}/shippingaddress/${shippingAddressId}`,
                shippingAddress,
                getHttpConfig()
            ).then(response => response.data);
        }

        function deleteShippingAddress(shippingAddressId) {
            return $http.delete(
                `${apiUrls.shopperAuth}/shippingaddress/${shippingAddressId}`,
                getHttpConfig()
            );
        }

        function getShippingMethods() {
            return $http.get(apiUrls.shippingMethods, getHttpConfig(true)).then(response => response.data);
        }

        function getTax(cartId, shippingPrice, shippingAddress) {
            return $http.post(`${apiUrls.cart}/${cartId}/taxes`, { shippingPrice, shippingAddress }, getHttpConfig())
                .then(response => response.data);
        }

        function verifyAddress(address) {
            if (!address) return Promise.reject();

            return $http.post(`${apiUrls.addressVerification}`, {
                address1: address.address1,
                address2: address.address2,
                locality: address.city,
                administrativeArea: address.state,
                country: address.country || 'US',
                postalCode: address.postalCode
            }, getHttpConfig()).then(response => response.data);
        }

        function autocompleteAddress(address, components) {
            if (!address) return Promise.reject();

            const escapedAddress = encodeURIComponent(address);

            return $http.get(
                `${apiUrls.addressAutocomplete}?input=${escapedAddress}&components=${components}`,
                getHttpConfig(true)
            ).then(response => response.data.matches);
        }

        function getAutocompleteAddressParts(addressId) {
            if (!addressId) return Promise.reject();

            const escapedAddressId = encodeURIComponent(addressId);

            return $http.get(`${apiUrls.addressAutocompleteDetails}?placeId=${escapedAddressId}`);
        }

        return {
            setTenantId,
            getTenantId,
            setShopperId,
            getShopperId,
            getLegacyCart,
            getCart,
            fetchShippingMethods,
            updateLegacyCart,
            updateCart,
            getClientId,
            getBag,
            createOrder,
            getOrder,
            legacyCreatePaypalExpressCheckout,
            createPaypalExpressCheckout,
            getPaypalExpressCheckout,
            getPayPalClientToken,
            getStoreInformation,
            getStripePublishableKey,
            getDiscountValidity,
            addDiscountCodeToCart,
            removeDiscountCodeFromCart,
            getStoreCreditByCode,
            getShipToLocations,
            getShippingMethods,
            getTax,
            autocompleteAddress,
            getAutocompleteAddressParts,
            verifyAddress,
            account: {
                login,
                isEmailRegistered,
                sendPasswordResetEmail,
                verifyResetPasswordCode,
                updatePassword,
                create: createShopper,
                get: getShopper,
                update: updateShopper,
                shippingAddress: {
                    add: addShippingAddress,
                    get: getShippingAddressById,
                    getAll: getShippingAddresses,
                    update: updateShippingAddress,
                    delete: deleteShippingAddress
                },
                paymentMethod: {
                    add: addPaymentMethod,
                    get: getPaymentMethodById,
                    getAll: getPaymentMethods,
                    update: updatePaymentMethod,
                    delete: deletePaymentMethod
                }
            }
        };
    }).name;



// WEBPACK FOOTER //
// ./src/app/services/api.service.js